import {
  Button,
  ConfirmationModal,
  Input,
  StyledReactSelect,
} from '@fountain/fountain-ui-components';
import { Grid, IconButton, Typography } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  ScoreCardTemplatesStage,
  WorkflowCustomStage,
  WorkflowSchedulerStage,
  WorkflowSchedulerV2Stage,
  WorkflowStageDetail,
} from 'api-clients/monolith';
import { useSimpleToggle } from 'hooks';
import produce from 'immer';
import React, { useContext, useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Error } from 'components/Error';
import { StageSettingCard } from 'containers/WorkflowEditor/components/StageSettingCard';
import { SettingSwitch } from 'containers/WorkflowEditor/components/WorkflowEditorContent/SettingSwitch';
import {
  StageContext,
  StageContextData,
} from 'containers/WorkflowEditor/contexts/stageContext';
import globalMessages from 'shared/global/messages';

import { AddScorecardModal } from './AddScorecardModal';
import { messages } from './messages';
import { useStyles } from './styles';

type RequiredSubmissionsMode =
  | 'all_evaluators'
  | 'at_least_one'
  | 'specific_number';

const DEFAULT_REQUIRED_SUBMISSIONS: RequiredSubmissionsMode = 'all_evaluators';
const DEFAULT_REQUIRED_SUBMISSIONS_COUNT = 2;

export type ScorecardStage =
  | WorkflowCustomStage
  | WorkflowSchedulerStage
  | WorkflowSchedulerV2Stage;

export interface ScorecardCardProps {
  stage: ScorecardStage;
  setStage: React.Dispatch<React.SetStateAction<WorkflowStageDetail>>;
}

export const ScorecardCard: VFC<ScorecardCardProps> = ({ stage, setStage }) => {
  const intl = useIntl();
  const styles = useStyles();

  // The card also renders outside a StageContextProvider (tests, stories); the
  // context only carries the save errors, so it is optional here.
  const stageContext = useContext(StageContext) as StageContextData | undefined;
  const updateStageResult = stageContext?.updateStageResult;

  const {
    showContent: isModalOpen,
    on: openModal,
    off: closeModal,
  } = useSimpleToggle();
  const [pendingRemoval, setPendingRemoval] = useState<{
    templateId: number;
    name: string;
  } | null>(null);

  const availableTemplates =
    stage.additional_info.available_score_card_templates ?? [];
  const assignments =
    stage.additional_info.score_cards_templates_stages_attributes ?? [];

  const liveAssignments = assignments.filter(
    assignment => !assignment._destroy,
  );
  const assignedTemplateIds = liveAssignments
    .map(assignment => assignment.template_id)
    .filter((id): id is number => id != null);

  const addableTemplates = availableTemplates.filter(
    template =>
      template.id != null && !assignedTemplateIds.includes(template.id),
  );

  const rows = liveAssignments.map(assignment => ({
    assignment,
    template: availableTemplates.find(
      template => template.id === assignment.template_id,
    ),
  }));

  const handleAdd = (templateId: number) => {
    setStage(
      produce(stage, draftStage => {
        if (
          draftStage.type !== 'CustomStage' &&
          draftStage.type !== 'SchedulerStage' &&
          draftStage.type !== 'SchedulerV2Stage'
        ) {
          return;
        }

        const attrs =
          draftStage.additional_info.score_cards_templates_stages_attributes ??
          [];

        const marked = attrs.find(
          assignment =>
            assignment._destroy && assignment.template_id === templateId,
        );

        if (marked) {
          delete marked._destroy;
        } else {
          attrs.push({ template_id: templateId });
        }

        draftStage.additional_info.score_cards_templates_stages_attributes =
          attrs;
      }),
    );
    closeModal();
  };

  const handleRemove = (templateId: number) => {
    setStage(
      produce(stage, draftStage => {
        if (
          draftStage.type !== 'CustomStage' &&
          draftStage.type !== 'SchedulerStage' &&
          draftStage.type !== 'SchedulerV2Stage'
        ) {
          return;
        }

        const attrs =
          draftStage.additional_info.score_cards_templates_stages_attributes ??
          [];
        const index = attrs.findIndex(
          assignment =>
            !assignment._destroy && assignment.template_id === templateId,
        );
        if (index === -1) return;

        if (attrs[index].id != null) {
          attrs[index]._destroy = true;
        } else {
          attrs.splice(index, 1);
        }

        draftStage.additional_info.score_cards_templates_stages_attributes =
          attrs;

        // A requirement with no scorecard attached can never be satisfied, and
        // the toggle that would clear it only renders while one is attached.
        if (attrs.every(assignment => assignment._destroy)) {
          draftStage.additional_info.require_score_cards = false;
        }
      }),
    );
    setPendingRemoval(null);
  };

  const rowKey = (assignment: ScoreCardTemplatesStage) =>
    assignment.id ?? `new-${assignment.template_id ?? 0}`;

  const requireScoreCards = Boolean(stage.additional_info.require_score_cards);
  const requiredSubmissions =
    stage.additional_info.score_cards_required_submissions ??
    DEFAULT_REQUIRED_SUBMISSIONS;
  const requiredSubmissionsCount =
    stage.additional_info.score_cards_required_submissions_count ??
    DEFAULT_REQUIRED_SUBMISSIONS_COUNT;

  const requiredSubmissionsOptions: Array<{
    value: RequiredSubmissionsMode;
    label: string;
  }> = [
    {
      value: 'all_evaluators',
      label: intl.formatMessage(messages.allAssignedEvaluators),
    },
    {
      value: 'at_least_one',
      label: intl.formatMessage(messages.atLeastOneSubmission),
    },
    {
      value: 'specific_number',
      label: intl.formatMessage(messages.atLeastASpecificNumber),
    },
  ];

  const isScorecardStage = (
    draftStage: WorkflowStageDetail,
  ): draftStage is ScorecardStage =>
    draftStage.type === 'CustomStage' ||
    draftStage.type === 'SchedulerStage' ||
    draftStage.type === 'SchedulerV2Stage';

  const handleToggleRequireScoreCards = (e: {
    target: { checked: boolean };
  }) => {
    const checked = !!e.target.checked;

    setStage(
      produce(stage, draftStage => {
        if (!isScorecardStage(draftStage)) return;

        draftStage.additional_info.require_score_cards = checked;

        if (
          checked &&
          draftStage.additional_info.score_cards_required_submissions == null
        ) {
          draftStage.additional_info.score_cards_required_submissions =
            DEFAULT_REQUIRED_SUBMISSIONS;
        }
      }),
    );
  };

  const handleChangeRequiredSubmissions = (option: {
    value: RequiredSubmissionsMode;
  }) => {
    setStage(
      produce(stage, draftStage => {
        if (!isScorecardStage(draftStage)) return;

        draftStage.additional_info.score_cards_required_submissions =
          option.value;

        if (
          option.value === 'specific_number' &&
          draftStage.additional_info.score_cards_required_submissions_count ==
            null
        ) {
          draftStage.additional_info.score_cards_required_submissions_count =
            DEFAULT_REQUIRED_SUBMISSIONS_COUNT;
        }
      }),
    );
  };

  const handleChangeRequiredSubmissionsCount = (e: {
    target: { value: string };
  }) => {
    const count = parseInt(e.target.value, 10) || 0;

    setStage(
      produce(stage, draftStage => {
        if (!isScorecardStage(draftStage)) return;

        draftStage.additional_info.score_cards_required_submissions_count =
          count;
      }),
    );
  };

  const hasRequiredSubmissionsCountError =
    updateStageResult?.isError === true &&
    updateStageResult.error?.errors?.score_cards_required_submissions_count !==
      undefined;

  return (
    <StageSettingCard
      title={<FormattedMessage {...messages.scorecard} />}
      variant="default"
    >
      <Typography variant="body2" className={styles.description}>
        <FormattedMessage {...messages.description} />
      </Typography>

      {rows.length > 0 && (
        <Grid container direction="column" className={styles.rows}>
          {rows.map(({ assignment, template }) => {
            const name =
              template?.name ??
              intl.formatMessage(messages.unavailableScorecard);
            const templateId = assignment.template_id ?? 0;

            return (
              <Grid
                key={rowKey(assignment)}
                container
                alignItems="center"
                justify="space-between"
                wrap="nowrap"
                className={styles.row}
                data-testid="scorecard-stage-row"
              >
                <Typography variant="h4" className={styles.name}>
                  {name}
                </Typography>
                <IconButton
                  size="small"
                  className={styles.deleteButton}
                  aria-label={intl.formatMessage(globalMessages.remove)}
                  title={intl.formatMessage(globalMessages.remove)}
                  onClick={() => setPendingRemoval({ templateId, name })}
                >
                  <DeleteIcon style={{ fontSize: 16 }} />
                </IconButton>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Button
        type="secondary"
        size="small"
        autoWidth
        className={styles.addButton}
        onClick={openModal}
        disabled={addableTemplates.length === 0}
      >
        <Typography variant="h4">
          <FormattedMessage {...messages.addScorecard} />
        </Typography>
      </Button>

      {rows.length > 0 && (
        <Grid className={styles.requirementSection}>
          <SettingSwitch
            dataKey="require_score_cards"
            checked={requireScoreCards}
            onChange={handleToggleRequireScoreCards}
            label={
              <>
                <FormattedMessage {...messages.requireScoreCards} />
                <Typography
                  component="span"
                  variant="caption"
                  className={styles.requirementDescription}
                >
                  <FormattedMessage {...messages.requireScoreCardsDescription} />
                </Typography>
              </>
            }
            fieldComponent={
              requireScoreCards ? (
                <Grid className={styles.requirementFields}>
                  <Grid className={styles.submissionsSelect}>
                    <StyledReactSelect
                      aria-label={intl.formatMessage(
                        messages.requiredSubmissions,
                      )}
                      label={intl.formatMessage(messages.requiredSubmissions)}
                      options={requiredSubmissionsOptions}
                      value={requiredSubmissionsOptions.find(
                        option => option.value === requiredSubmissions,
                      )}
                      onChange={handleChangeRequiredSubmissions}
                    />
                  </Grid>
                  {requiredSubmissions === 'specific_number' && (
                    <Grid className={styles.countInput}>
                      <Input
                        type="number"
                        value={requiredSubmissionsCount}
                        onChange={handleChangeRequiredSubmissionsCount}
                        error={hasRequiredSubmissionsCountError}
                        inputProps={{
                          min: 1,
                          'aria-label': intl.formatMessage(
                            messages.atLeastASpecificNumber,
                          ),
                          'data-testid': 'required-submissions-count-input',
                        }}
                      />
                      <Error
                        error={
                          hasRequiredSubmissionsCountError &&
                          intl.formatMessage(
                            messages.requiredSubmissionsCountError,
                          )
                        }
                        align="right"
                      />
                    </Grid>
                  )}
                </Grid>
              ) : null
            }
          />
        </Grid>
      )}

      {isModalOpen && (
        <AddScorecardModal
          templates={addableTemplates}
          assignee={
            stage.type === 'CustomStage' ? 'opening_owner' : 'interview_host'
          }
          onAdd={handleAdd}
          onClose={closeModal}
        />
      )}

      {pendingRemoval && (
        <ConfirmationModal
          negative
          modalTitle={intl.formatMessage(messages.removeScorecard)}
          modalContentText={intl.formatMessage(
            messages.removeScorecardConfirmation,
            { name: pendingRemoval.name },
          )}
          actionButtonText={intl.formatMessage(messages.removeScorecard)}
          cancelButtonText={intl.formatMessage(globalMessages.cancel)}
          handleClose={() => setPendingRemoval(null)}
          handleCancel={() => setPendingRemoval(null)}
          handleSubmit={() => handleRemove(pendingRemoval.templateId)}
        />
      )}
    </StageSettingCard>
  );
};
